import React, { useEffect, useMemo, useState } from "react";
import api, { formatINR } from "../lib/api";
import { payWithRazorpay, fetchRazorpayConfig } from "../lib/razorpay";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Stethoscope, MapPin, BadgeCheck, ExternalLink, Video, Building2, Lock } from "lucide-react";
import { StarRating } from "../components/StarRating";
import ReviewsDialog from "../components/ReviewsDialog";
import { toast, Toaster } from "sonner";

const ANY = "__any__";

export default function Doctors() {
    const [registered, setRegistered] = useState([]);
    const [internet, setInternet] = useState([]);
    const [cities, setCities] = useState([]); // [{city, areas, count}]
    const [city, setCity] = useState(ANY);
    const [area, setArea] = useState(ANY);
    const [spec, setSpec] = useState("");
    const [settings, setSettings] = useState({ show_internet_doctors: true });
    const [ratings, setRatings] = useState({});
    const [gateway, setGateway] = useState({ enabled: false, key_id: "", mode: null });

    const fetchAll = async (cityVal = city, areaVal = area, specVal = spec) => {
        const params = {
            city: cityVal !== ANY ? cityVal : undefined,
            area: areaVal !== ANY ? areaVal : undefined,
            specialization: specVal || undefined,
        };
        const [r, i, s, c] = await Promise.all([
            api.get("/doctors/registered", { params }),
            api.get("/doctors/internet", { params }),
            api.get("/settings/public"),
            api.get("/doctors/internet/cities"),
        ]);
        setRegistered(r.data);
        setInternet(i.data);
        setSettings(s.data);
        setCities(c.data);
        // Bulk-load review summaries for all doctors on screen (both buckets)
        const allIds = [...r.data, ...i.data].map(d => d.id).filter(Boolean);
        if (allIds.length) {
            try {
                const sumRes = await api.get("/reviews/summary-many", { params: { target_type: "doctor", ids: allIds.join(",") } });
                setRatings(sumRes.data || {});
            } catch { /* non-blocking */ }
        }
    };

    useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

    useEffect(() => { fetchRazorpayConfig().then(setGateway); }, []);

    const areasForCity = useMemo(() => {
        if (city === ANY) return [];
        const match = cities.find((c) => c.city === city);
        return match?.areas || [];
    }, [city, cities]);

    const onCityChange = (v) => {
        setCity(v);
        setArea(ANY);
        fetchAll(v, ANY, spec);
    };
    const onAreaChange = (v) => {
        setArea(v);
        fetchAll(city, v, spec);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="doctors-page">
            <Toaster position="top-center" />
            <div className="label-eyebrow text-sky-700">Find a homeopath</div>
            <h1 className="font-serif text-5xl text-stone-900 mt-2 mb-2">Verified doctors, online & near you</h1>
            <p className="text-sm text-stone-600 max-w-2xl mb-8">
                Online consultation: <span className="text-stone-900 font-medium">{formatINR(settings.consultation_fee_online)}</span> &nbsp;·&nbsp;
                Offline visit: <span className="text-stone-900 font-medium">{formatINR(settings.consultation_fee_offline)}</span>
            </p>

            <div className="grid sm:grid-cols-4 gap-3 mb-8">
                <Select value={city} onValueChange={onCityChange}>
                    <SelectTrigger className="rounded-none border-stone-300" data-testid="filter-city"><SelectValue placeholder="Any city" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ANY}>Any city</SelectItem>
                        {cities.map((c) => (
                            <SelectItem key={c.city} value={c.city}>{c.city} <span className="text-stone-400 text-xs">({c.count})</span></SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={area} onValueChange={onAreaChange} disabled={city === ANY}>
                    <SelectTrigger className="rounded-none border-stone-300" data-testid="filter-area"><SelectValue placeholder={city === ANY ? "Pick a city first" : "Any area"} /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ANY}>Any area</SelectItem>
                        {areasForCity.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Input value={spec} onChange={(e) => setSpec(e.target.value)} placeholder="Specialization (e.g. skin)" className="rounded-none border-stone-300" data-testid="filter-spec" />
                <Button onClick={() => fetchAll()} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="filter-apply">Search</Button>
            </div>

            <Tabs defaultValue="registered" className="w-full" data-testid="doctors-tabs">
                <TabsList className="bg-stone-100 rounded-none border border-stone-200 p-0">
                    <TabsTrigger value="registered" className="rounded-none data-[state=active]:bg-sky-700 data-[state=active]:text-stone-50 px-6 py-3" data-testid="tab-registered">
                        <BadgeCheck className="w-4 h-4 mr-2" /> Registered ({registered.length})
                    </TabsTrigger>
                    {settings.show_internet_doctors && (
                        <TabsTrigger value="internet" className="rounded-none data-[state=active]:bg-stone-900 data-[state=active]:text-stone-50 px-6 py-3" data-testid="tab-internet">
                            <ExternalLink className="w-4 h-4 mr-2" /> Internet-sourced ({internet.length})
                        </TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="registered" className="mt-6">
                    {registered.length === 0 ? (
                        <Empty msg="No registered doctors yet. They'll appear after admin approval." />
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {registered.map((d) => <RegisteredCard key={d.id} d={d} fee={settings} rating={ratings[d.id]} gateway={gateway} />)}
                        </div>
                    )}
                </TabsContent>

                {settings.show_internet_doctors && (
                    <TabsContent value="internet" className="mt-6">
                        {internet.length === 0 ? (
                            <Empty msg="No internet-sourced doctors visible for this filter." />
                        ) : (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {internet.map((d) => <InternetCard key={d.id} d={d} rating={ratings[d.id]} />)}
                            </div>
                        )}
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}

const Empty = ({ msg }) => (
    <div className="border border-stone-200 bg-white p-12 text-center">
        <Stethoscope className="w-8 h-8 text-stone-300 mx-auto mb-3" />
        <p className="text-sm text-stone-500">{msg}</p>
    </div>
);

function RegisteredCard({ d, fee, rating, gateway = {} }) {
    const { user } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("online");
    const [date, setDate] = useState("");
    const [slot, setSlot] = useState("10:00");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    // Effective fee: doctor's own override → site fallback
    const onlineFee = d.fee_online != null ? d.fee_online : fee.consultation_fee_online;
    const offlineFee = d.fee_offline != null ? d.fee_offline : fee.consultation_fee_offline;
    const activeFee = mode === "online" ? onlineFee : offlineFee;

    const book = async () => {
        if (!user) { nav("/login?next=/doctors"); return; }
        if (!date) return toast.error("Pick a date");
        setBusy(true);
        try {
            const payload = { doctor_id: d.id, mode, date, time_slot: slot, notes };
            if (gateway.enabled && activeFee > 0) {
                await payWithRazorpay({ purpose: "appointment", appointment: payload });
                toast.success("Payment successful — booking confirmed. The doctor's contact details are now unlocked.");
            } else {
                await api.post("/appointments", payload);
                toast.success("Appointment booked! Doctor's contact details will be shared once you complete payment.");
            }
            setOpen(false);
        } catch (e) {
            if (e.cancelled) toast.error(e.message);
            else toast.error(e.response?.data?.detail || e.message || "Could not book");
        } finally { setBusy(false); }
    };

    return (
        <div className="border border-stone-200 bg-white p-6" data-testid={`reg-doctor-${d.id}`}>
            <div className="flex gap-4">
                <div className="w-16 h-16 bg-stone-100 shrink-0 overflow-hidden">
                    {d.photo_url && <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="font-serif text-xl text-stone-900">{d.name}</h3>
                        <BadgeCheck className="w-4 h-4 text-sky-700" />
                    </div>
                    <div className="text-xs text-stone-500">{d.qualification} · {d.specialization}</div>
                    <div className="text-xs text-stone-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{d.city}{d.state ? `, ${d.state}` : ""}</div>
                    <div className="mt-1.5">
                        <ReviewsDialog targetType="doctor" targetId={d.id} targetName={d.name} rating={rating} dataTestIdBase="reg-reviews" />
                    </div>
                </div>
            </div>
            {d.bio && <p className="text-xs text-stone-600 mt-3 line-clamp-3">{d.bio}</p>}
            <div className="flex gap-2 mt-4 flex-wrap">
                {d.online_consult && <span className="text-[10px] uppercase tracking-widest border border-sky-700 text-sky-700 px-2 py-1"><Video className="w-3 h-3 inline mr-1" />Online · ₹{onlineFee}</span>}
                {d.offline_consult && <span className="text-[10px] uppercase tracking-widest border border-stone-700 text-stone-700 px-2 py-1"><Building2 className="w-3 h-3 inline mr-1" />Clinic · ₹{offlineFee}</span>}
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 flex items-center gap-1.5">
                <Lock className="w-3 h-3" />
                Doctor's phone & clinic address unlock after booking + payment
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    <Button className="rounded-none w-full mt-4 bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid={`book-${d.id}`}>Book consultation</Button>
                </DialogTrigger>
                <DialogContent className="rounded-none">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Book with {d.name}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Mode</Label>
                            <div className="flex gap-2 mt-2">
                                {d.online_consult && <button onClick={() => setMode("online")} className={`px-4 py-2 text-sm border ${mode === "online" ? "border-sky-700 bg-sky-700 text-stone-50" : "border-stone-300"}`}>Online · ₹{onlineFee}</button>}
                                {d.offline_consult && <button onClick={() => setMode("offline")} className={`px-4 py-2 text-sm border ${mode === "offline" ? "border-sky-700 bg-sky-700 text-stone-50" : "border-stone-300"}`}>Clinic · ₹{offlineFee}</button>}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-stone-500">Date</Label>
                                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="appt-date" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-stone-500">Time</Label>
                                <Input type="time" value={slot} onChange={(e) => setSlot(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="appt-time" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Notes (optional)</Label>
                            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="appt-notes" />
                        </div>
                        <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <div>
                                {gateway.enabled
                                    ? <>Pay <strong>₹{activeFee}</strong> securely via UPI / card to confirm this slot. The doctor's phone &amp; clinic address unlock the moment payment succeeds.</>
                                    : <>Doctor's phone &amp; clinic address will be revealed only after your payment of <strong>₹{activeFee}</strong> is confirmed. This keeps doctors' lines protected from spam.</>}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={book} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 w-full" data-testid="confirm-book">
                            {busy
                                ? (gateway.enabled ? "Opening secure payment…" : "Booking…")
                                : (gateway.enabled ? `Pay ₹${activeFee} & confirm` : `Confirm booking · ₹${activeFee}`)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function InternetCard({ d, rating }) {
    return (
        <div className="border border-stone-200 bg-stone-50 p-6" data-testid={`int-doctor-${d.id}`}>
            <div className="flex gap-4">
                <div className="w-16 h-16 bg-stone-100 shrink-0 overflow-hidden">
                    {d.photo_url && <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                    <h3 className="font-serif text-xl text-stone-900">{d.name}</h3>
                    <div className="text-xs text-stone-500">{d.qualification} · {d.specialization}</div>
                    <div className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[d.area, d.city, d.state].filter(Boolean).join(", ")}
                    </div>
                    <div className="mt-1.5">
                        <ReviewsDialog targetType="doctor" targetId={d.id} targetName={d.name} rating={rating} dataTestIdBase="int-reviews" />
                    </div>
                </div>
            </div>
            {d.clinic_name && <div className="text-xs text-stone-600 mt-3">{d.clinic_name}{d.clinic_address ? ` · ${d.clinic_address}` : ""}</div>}
            {d.phone && <div className="text-xs text-stone-700 mt-2"><span className="text-stone-500 mr-1">Phone:</span>{d.phone}</div>}
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-4 border-t border-stone-200 pt-3 flex items-center justify-between">
                <span>Listed publicly · contact directly</span>
                {d.source_url && <a href={d.source_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline inline-flex items-center gap-1">Source <ExternalLink className="w-3 h-3" /></a>}
            </div>
        </div>
    );
}
